import React from 'react';
import { useTranslation } from 'react-i18next';

interface RouteLoadingShellProps {
  /**
   * `main` — fills Layout `<main>` / outlet (Navbar stays visible).
   * `standalone` — full viewport (pre-Layout Suspense / gates outside chrome).
   */
  variant?: 'main' | 'standalone';
  /** Optional status line under the brand (defaults to common.loading). */
  label?: string;
}

/**
 * Quiet route busy state aligned to #lcp-boot-app.
 * Prefer this over LoadingSpinner fullPage so chrome (Navbar) is not wiped.
 */
const RouteLoadingShell: React.FC<RouteLoadingShellProps> = ({
  variant = 'main',
  label,
}) => {
  const { t } = useTranslation();
  const status = label || t('common.loading');
  const shellClass =
    variant === 'standalone'
      ? 'min-h-[100dvh] max-h-[100dvh] w-full overflow-hidden flex flex-col items-center justify-center gap-4 px-6 py-8 text-center'
      : 'flex-1 w-full min-h-[40vh] flex flex-col items-center justify-center gap-4 px-6 py-12 text-center';

  return (
    <div
      className={shellClass}
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="route-loading-shell"
    >
      <p className="m-0 text-sm font-semibold tracking-wide text-primary-light">
        Architect-IQ
      </p>
      <div
        className="h-10 w-10 rounded-full border-[3px] border-white/10 border-t-primary-light animate-spin motion-reduce:animate-none motion-reduce:opacity-85"
        aria-hidden
      />
      <p className="m-0 text-sm font-medium text-gray-400">{status}</p>
    </div>
  );
};

export default RouteLoadingShell;
